import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

import { useSizeTypes } from 'contexts';
import type { Container } from 'types';
import {
  BookingReeferView,
  ReeferAutofillKey,
  ReeferField,
  ReeferOwnership,
  bookingReeferView,
  planReeferAutofill,
  sameReeferAutofillKey,
  usableBookingReefer
} from 'utils';

export type ReeferBookingAutofill = {
  /** The attached booking's reefer instructions for the panel; null without a resolved booking. */
  reeferInstructions: BookingReeferView | null;
  /** Records the booking's value in a field at the inspector's request. The value is then theirs. */
  applyBookingReefer: (field: ReeferField) => void;
  /** Marks a field as edited by the inspector, so no booking change ever touches it again. */
  releaseReeferField: (field: ReeferField) => void;
};

const without = (owned: ReeferOwnership, field: ReeferField): ReeferOwnership => {
  const next = { ...owned };
  delete next[field];
  return next;
};

/**
 * Fills the container's empty reefer fields from its booking, and keeps booking-filled values following
 * the booking for the rest of the form session (openspec gate-reefer-booking-autofill).
 *
 * Fills happen only when the key changes — booking id, reefer by the catalog, unit id — never on a
 * validation. The unit and booking fields re-validate whenever their value changes, including when a
 * draft is reopened, so a fill placed on those paths would also run on every reopen.
 *
 * `baseline` is bumped by restore and reset in the same render as the containers they set. On a bump
 * the current state becomes the starting point: nothing is filled, and everything restored is the
 * inspector's.
 */
const useReeferBookingAutofill = (
  container: Container,
  setContainer: Dispatch<SetStateAction<Container>>,
  baseline: number
): ReeferBookingAutofill => {
  const { isReefer } = useSizeTypes();
  const containerIsReefer = isReefer(container.sizeType);
  const bookingId = typeof container.booking?.id === 'number' ? container.booking.id : null;
  const unitId = (container.id ?? 0) > 0 ? container.id : null;

  const key: ReeferAutofillKey = useMemo(
    () => ({ bookingId, isReefer: containerIsReefer, unitId }),
    [bookingId, containerIsReefer, unitId]
  );

  const keyRef = useRef(key);
  const baselineRef = useRef(baseline);
  /* what each booking-filled field was filled with; session memory only, by design */
  const owned = useRef<ReeferOwnership>({});

  useEffect(() => {
    if (baselineRef.current !== baseline) {
      baselineRef.current = baseline;
      keyRef.current = key;
      owned.current = {};
      return;
    }
    const prevKey = keyRef.current;
    if (sameReeferAutofillKey(prevKey, key)) return;
    keyRef.current = key;

    /*
     * Plan against the latest container, inside the updater, so a unit or booking lookup landing in the
     * same moment is not overwritten. The ownership it starts from is captured here, so the updater
     * gives the same answer if React calls it twice.
     */
    const prevOwned = owned.current;
    setContainer(c => {
      const plan = planReeferAutofill(prevKey, key, c, prevOwned, usableBookingReefer(c.booking));
      owned.current = plan.owned;
      return Object.keys(plan.patch).length ? { ...c, ...plan.patch } : c;
    });
  }, [baseline, key, setContainer]);

  const applyBookingReefer = useCallback(
    (field: ReeferField) => {
      owned.current = without(owned.current, field);
      setContainer(c => {
        const suggestion = usableBookingReefer(c.booking)[field];
        return suggestion === undefined ? c : { ...c, [field]: suggestion };
      });
    },
    [setContainer]
  );

  const releaseReeferField = useCallback((field: ReeferField) => {
    owned.current = without(owned.current, field);
  }, []);

  const reeferInstructions = useMemo(
    () => bookingReeferView(container.booking, container, containerIsReefer),
    [container, containerIsReefer]
  );

  return { reeferInstructions, applyBookingReefer, releaseReeferField };
};

export default useReeferBookingAutofill;
